import { z } from 'zod';
import { CurrencySchema, MoneyStringSchema } from './common.js';

export const ReceberSchema = z.object({
  valor: MoneyStringSchema,
  /** Token idempotente do cliente — evita duplicar recebimento em retry (RNF-14). */
  idempotencyKey: z.string().min(8),
});
export type ReceberDTO = z.infer<typeof ReceberSchema>;

export const EstornarSchema = z.object({
  justificativa: z.string().min(1),
});
export type EstornarDTO = z.infer<typeof EstornarSchema>;

export const ExecutarSchema = z.object({
  accountId: z.string().uuid(),
  valorOrcado: MoneyStringSchema,
  valorExecutado: MoneyStringSchema,
  /** Token idempotente do cliente — evita duplicar pagamento em retry (RNF-14). */
  idempotencyKey: z.string().min(8),
});
export type ExecutarDTO = z.infer<typeof ExecutarSchema>;

export const RelatorioMargemSchema = z.object({
  quoteId: z.string().uuid(),
  precoFinal: MoneyStringSchema,
  custoPrevisto: MoneyStringSchema,
  custoExecutado: MoneyStringSchema,
  margemPrevista: z.number(),
  margemExecutada: z.number(),
});
export type RelatorioMargemDTO = z.infer<typeof RelatorioMargemSchema>;

export const CaixaBalanceSchema = z.object({
  accountId: z.string().uuid(),
  kind: z.string(),
  nome: z.string(),
  previsto: MoneyStringSchema,
  efetivado: MoneyStringSchema,
  executado: MoneyStringSchema,
  abertura: MoneyStringSchema,
});
export type CaixaBalanceDTO = z.infer<typeof CaixaBalanceSchema>;

/** Criação de um centro de custo personalizado (kind `personalizado`) — RF-FIN-01. */
export const CreateCaixaSchema = z.object({
  nome: z.string().min(1).max(80),
  moeda: CurrencySchema.optional(),
  /** Centro de custo pai opcional (para aninhar sob um existente). */
  parentId: z.string().uuid().optional(),
});
export type CreateCaixaDTO = z.infer<typeof CreateCaixaSchema>;

export const UpdateCaixaSchema = z.object({
  nome: z.string().min(1).max(80),
});
export type UpdateCaixaDTO = z.infer<typeof UpdateCaixaSchema>;

/** Item da listagem administrável de centros de custo (inclui a flag de personalizado). */
export const CaixaViewSchema = z.object({
  id: z.string().uuid(),
  kind: z.string(),
  nome: z.string(),
  moeda: CurrencySchema,
  parentId: z.string().uuid().nullable(),
  /** Personalizado = criado pelo usuário; pode ser renomeado/removido. Sistema = fixo. */
  personalizado: z.boolean(),
});
export type CaixaViewDTO = z.infer<typeof CaixaViewSchema>;

export const ConsolidadoSchema = z.object({
  caixas: z.array(CaixaBalanceSchema),
  totalPrevisto: MoneyStringSchema,
  totalEfetivado: MoneyStringSchema,
  totalAbertura: MoneyStringSchema,
  /** Total conciliável com o banco = efetivado + abertura (I2, RF-FIN-17). */
  totalConciliacao: MoneyStringSchema,
});
export type ConsolidadoDTO = z.infer<typeof ConsolidadoSchema>;

export const AberturaSchema = z.object({
  saldos: z.array(z.object({ accountId: z.string().uuid(), valor: MoneyStringSchema })).min(1),
  saldoBanco: MoneyStringSchema,
});
export type AberturaDTO = z.infer<typeof AberturaSchema>;

export const QuoteFinanceiroSchema = z.object({
  quoteId: z.string().uuid(),
  caixas: z.array(CaixaBalanceSchema),
  totalPrevisto: MoneyStringSchema,
  totalEfetivado: MoneyStringSchema,
});
export type QuoteFinanceiroDTO = z.infer<typeof QuoteFinanceiroSchema>;

export const CoberturaFolhaSchema = z.object({
  folhaMediaMensal: MoneyStringSchema,
  saldoEfetivado: MoneyStringSchema,
  saldoComPrevisto: MoneyStringSchema,
  mesesCobertosEfetivado: z.number(),
  mesesCobertosComPrevisto: z.number(),
});
export type CoberturaFolhaDTO = z.infer<typeof CoberturaFolhaSchema>;
